<template>
  <div class="wait-view-container">
    <div
      v-if="gameState.roundIndex < 0"
    >
      <WaitViewAnimation />
      <div class="info-container">
        <div class="wait-message">
          {{ statusMessage }}
        </div>
        <div class="game-roomcode-item">
          <transition name="pop-from-top">
            <div
              v-show="displayCopiedMessage"
              class="copied-message-item"
            >
              Copied room code!
            </div>
          </transition>
          Room Code: {{ gameRoomcode }}
          <span
            v-if="isClipboardAPIAvailable"
            @click="copyRoomcodeToClipboard"
            class="copy-button"
          >
            <font-awesome-icon :icon="['fas', 'copy']" />
          </span>
        </div>
        <div class="fixed-height-container">
          <div
            v-if="!gameHasFreePlayers && isHost && havePromptPacksLoaded"
            class="selector-container"
          >
            <PromptPackCustomSelect
              v-model="selectedPromptPack"
              :options="promptPacks"
            />
          </div>
          <div
            v-if="gameHasFreePlayers && isHost"
            class="free-players-container"
          >
            This room contains players on the basic plan
          </div>
        </div>
        <transition name="pop-from-top">
          <div
            v-show="canStartGame"
            class="start-game-button"
            @click="startGame"
          >
            <span
              v-show="!startGameButtonClicked"
            >
              Start Game
            </span>
            <Spinner
              v-show="startGameButtonClicked"
            />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import WaitViewAnimation from '@/components/gameComponents/WaitViewAnimation.vue';
import PromptPackCustomSelect from '@/components/utilComponents/PromptPackCustomSelect.vue';
import Spinner from '@/components/utilComponents/Spinner';

export default Vue.extend({
  name: 'WaitView',
  components: {
    WaitViewAnimation,
    PromptPackCustomSelect,
    Spinner
  },
  props: {
    gameRoomcode: String,
    gameState: Object,
    isHost: Boolean
  },
  data: () => ({
    isClipboardAPIAvailable: false,
    displayCopiedMessage: false,
    gameHasFreePlayers: false,
    defaultPromptPacks: [
      { promptPackId: 'allPrompts', promptPackName: 'All prompts' },
      // currently not allowing players to play with free prompts only.
      // { promptPackId: 'allFreePromptsOnly', promptPackName: 'All free prompts only' } 
    ],
    customOnlyPromptPack: { promptPackId: 'allCustomPromptsOnly', promptPackName: 'All custom prompts only' },
    promptPacks: [],
    selectedPromptPack: null,
    startGameButtonClicked: false,
    havePromptPacksLoaded: false
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    statusMessage: function () {
       if ('gameStatus' in this.gameState) {
          if (this.gameState.gameStatus === 'waitingForPlayers') {
            return 'Waiting for at least three players to join';
          } else if (this.gameState.gameStatus === 'waitingToStart') {
            if (this.isHost) {
              return 'Waiting for you to start the game';
            }
            return 'Waiting for the host to start the game';
          }
       }
       return '';
    },
    canStartGame: function () {
      return this.isHost && this.gameState.gameStatus === 'waitingToStart';
    }
  },
  watch: {
    gameState: async function () {
      if (this.isHost) {
        await this.checkGameHasFreePlayers();
      }
    }
  },
  methods: {
    async copyRoomcodeToClipboard() {
      await navigator.clipboard.writeText(this.gameRoomcode);
      this.displayCopiedMessage = true;
      await setTimeout(() => {
        this.displayCopiedMessage = false;
      }, 2000);
    },
    startGame() {
      this.startGameButtonClicked = true;
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/startgame`, {
        auth0UserId: this.auth0UserId,
        gameRoomcode: this.gameRoomcode,
        selectedPromptPackId: this.gameHasFreePlayers || !this.selectedPromptPack ? null : this.selectedPromptPack.promptPackId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).catch(error => {
        console.log(error);
        this.startGameButtonClicked = false;
      });
    },
    async checkGameHasFreePlayers() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/checkgamehasfreeplayers`, {
          gameRoomcode: this.gameRoomcode
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        this.gameHasFreePlayers = response.data.gameHasFreePlayers;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async getPromptPacks() {
      const canPlayWithCustomPromptsOnly = await this.checkCanPlayWithCustomPromptsOnly();
      const playablePromptPacks = await this.getPlayablePromptPacks();
      const promptPacks = [];
      promptPacks.push(...this.defaultPromptPacks);

      if (canPlayWithCustomPromptsOnly) {
        promptPacks.push(this.customOnlyPromptPack);
      }

      promptPacks.push(...playablePromptPacks);
      this.promptPacks = promptPacks;
      this.havePromptPacksLoaded = true;
    },
    async checkCanPlayWithCustomPromptsOnly() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/checkcanplaywithcustompromptsonly`, {
          auth0UserId: this.auth0UserId
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        return response.data.canPlayWithCustomPromptsOnly;
      } catch (error) {
        console.log('Error:', error);
        return false;
      }
    },
    async getPlayablePromptPacks() {
      try {
        const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getplayablepromptpacks`, {
          auth0UserId: this.auth0UserId,
          gameRoomcode: this.gameRoomcode
        }, {
          headers: {
            'Content-Type': 'application/json', 
            'Authorization': `Bearer ${this.idToken}`
          }
        });

        return response.data.promptPacks;
      } catch (error) {
        console.log('Error:', error);
        return [];
      }
    },
  },
  created: async function() {
    this.isClipboardAPIAvailable = !!navigator.clipboard;

    if (this.isHost) {
      await this.checkGameHasFreePlayers();
      await this.getPromptPacks();
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/wait-view-styles.scss';
</style>
